/**
 * useCustomerAdminMessages Hook
 * Customer-side hook for viewing and replying to admin support messages
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { attachmentPlaceholder, validateAttachment } from '../lib/messages/attachments.js'

export function useCustomerAdminMessages() {
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const channelRef = useRef(null)
  const { user } = useAuth()

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    if (!user) return

    try {
      setIsLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('admin_messages')
        .select(`
          *,
          sender:users!sender_id(id, full_name, avatar_url)
        `)
        .eq('user_id', user.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })

      if (fetchError) throw fetchError

      setMessages(data || [])
    } catch (err) {
      console.error('Error fetching admin messages:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [user])

  // Send message (customer or talent replies to admin), optionally with one attachment (JPEG, PNG,
  // PDF; 10 MB). The file goes through the server route so the storage write is checked and recorded
  // on the row; the realtime UPDATE then fills the bubble in.
  const sendMessage = useCallback(async (messageText, file = null) => {
    const text = (messageText || '').trim() || (file ? attachmentPlaceholder(file.name) : '')
    if (!user || !text) {
      return { success: false, error: 'Message text is required' }
    }
    if (file) {
      const check = validateAttachment(file)
      if (!check.ok) return { success: false, error: check.error }
    }

    try {
      // Get admin_id from existing messages (the admin who initiated the conversation)
      const adminId = messages.length > 0 ? messages[0].admin_id : null

      if (!adminId) {
        console.error('Cannot reply: No admin_id found in conversation')
        return { success: false, error: 'Cannot find conversation admin' }
      }

      const { data, error: insertError } = await supabase
        .from('admin_messages')
        .insert({
          admin_id: adminId, // Required: the admin who started this conversation
          user_id: user.id,
          sender_id: user.id, // Customer is sender
          message_text: text,
          is_read_admin: false, // Admin hasn't read it yet
          is_read_user: true // Customer has read their own message
        })
        .select(`
          *,
          sender:users!sender_id(id, full_name, avatar_url)
        `)
        .single()

      if (insertError) throw insertError

      if (file && data?.id) {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return { success: false, error: 'Message sent, but the file did not upload: not signed in' }
        const form = new FormData()
        form.append('file', file)
        const response = await fetch(`/api/admin-messages/${data.id}/attachment`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: form
        })
        const result = await response.json().catch(() => ({}))
        if (!response.ok) {
          console.error('Error uploading attachment:', result)
          return { success: false, error: `Message sent, but the file did not upload: ${result.message || response.status}` }
        }
        return { success: true, message: { ...data, ...result } }
      }

      // Message will be added via real-time subscription
      return { success: true, message: data }
    } catch (err) {
      console.error('Error sending message:', err)
      return { success: false, error: err.message }
    }
  }, [user, messages])

  // Mark admin messages as read
  const markAsRead = useCallback(async (messageId) => {
    if (!messageId) return

    try {
      const { error: updateError } = await supabase
        .from('admin_messages')
        .update({
          is_read_user: true,
          read_at_user: new Date().toISOString()
        })
        .eq('id', messageId)

      if (updateError) throw updateError

      return { success: true }
    } catch (err) {
      console.error('Error marking message as read:', err)
      return { success: false, error: err.message }
    }
  }, [])

  // Mark all unread messages as read
  const markAllAsRead = useCallback(async () => {
    if (!user) return

    try {
      // Find all unread messages sent by admin
      const unreadMessages = messages.filter(msg => {
        return msg.sender_id !== user.id && !msg.is_read_user
      })

      // Mark each as read
      for (const msg of unreadMessages) {
        await markAsRead(msg.id)
      }

      return { success: true }
    } catch (err) {
      console.error('Error marking all as read:', err)
      return { success: false, error: err.message }
    }
  }, [user, messages, markAsRead])

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return

    fetchMessages()

    // Subscribe to admin_messages changes
    const channel = supabase
      .channel(`customer_admin_messages:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_messages',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          // Fetch sender info
          const { data: sender } = await supabase
            .from('users')
            .select('id, full_name, avatar_url')
            .eq('id', payload.new.sender_id)
            .single()

          const newMessage = {
            ...payload.new,
            sender
          }

          setMessages(prev => {
            if (prev.some(msg => msg.id === newMessage.id)) {
              return prev
            }
            return [...prev, newMessage]
          })
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'admin_messages',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          setMessages(prev =>
            prev.map(msg =>
              msg.id === payload.new.id
                ? { ...msg, ...payload.new }
                : msg
            )
          )
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true)
          console.log(`✅ Subscribed to admin support chat`)
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          setIsConnected(false)
          console.error(`❌ Admin chat subscription error:`, status)
        }
      })

    channelRef.current = channel

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        console.log(`🔌 Unsubscribed from admin support chat`)
      }
    }
  }, [user, fetchMessages])

  return {
    messages,
    isLoading,
    error,
    isConnected,
    sendMessage,
    markAsRead,
    markAllAsRead,
    refetch: fetchMessages
  }
}
